import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, RefreshCw, Users, Shield, Zap, Bell, BellOff } from 'lucide-react';
import { apiService } from '../../services/apiService';
import { formatDateTimeWithYear } from '../../utils/format';

interface AdminPageProps {
  onBack: () => void;
}

interface UserRecord {
  uid: string;
  email: string;
  name: string;
  role: string;
  created_at: string | null;
  last_login: string | null;
  login_count: number;
  newsletter_enabled: boolean;
}

type AdminTab = 'users';

const AdminPage: React.FC<AdminPageProps> = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState<AdminTab>('users');
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [recalcResult, setRecalcResult] = useState<any[] | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getAdminUsers();
      setUsers(data || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleRecalculate = useCallback(async () => {
    try {
      setRecalculating(true);
      setRecalcResult(null);
      const data = await apiService.recalculatePerformance();
      setRecalcResult(data.recalculated || []);
    } catch (err) {
      console.error('Failed to recalculate:', err);
    } finally {
      setRecalculating(false);
    }
  }, []);

  // 7.3: shared by the desktop table row and the mobile card so the optimistic
  // update + rollback logic exists exactly once.
  const toggleNewsletter = async (user: UserRecord) => {
    const newEnabled = user.newsletter_enabled === false;
    setUsers(prev => prev.map(u => u.uid === user.uid ? { ...u, newsletter_enabled: newEnabled } : u));
    try {
      await apiService.adminToggleNewsletter(user.uid, newEnabled);
    } catch (err) {
      console.error('Failed to toggle:', err);
      setUsers(prev => prev.map(u => u.uid === user.uid ? { ...u, newsletter_enabled: !newEnabled } : u));
    }
  };

  const formatDate = (dateStr: string | null) =>
    dateStr ? formatDateTimeWithYear(dateStr) : '—';

  const timeAgo = (dateStr: string | null) => {
    if (!dateStr) return '—';
    try {
      const now = new Date();
      const d = new Date(dateStr);
      const diffMs = now.getTime() - d.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      if (diffMins < 60) return `${diffMins}m ago`;
      const diffHrs = Math.floor(diffMins / 60);
      if (diffHrs < 24) return `${diffHrs}h ago`;
      const diffDays = Math.floor(diffHrs / 24);
      if (diffDays < 30) return `${diffDays}d ago`;
      return formatDate(dateStr);
    } catch { return dateStr || '—'; }
  };

  return (
    <div className="min-h-[100dvh] bg-slate-950 pt-[56px]">
      {/* Header */}
      <div className="sticky top-[56px] z-40 bg-slate-950/95 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5 text-slate-400" />
            </button>
            <div className="flex-1">
              <h1 className="text-base font-bold text-white flex items-center gap-2">
                <Shield className="w-4 h-4 text-red-400" />
                Admin Panel
              </h1>
              <p className="text-11 text-slate-500">{users.length} users</p>
            </div>
            <button
              onClick={handleRecalculate}
              disabled={recalculating}
              className="flex items-center gap-1.5 px-3 py-1.5 text-11 font-medium bg-amber-500/15 text-amber-400 border border-amber-500/30 rounded-lg hover:bg-amber-500/25 transition-all disabled:opacity-50"
            >
              <Zap className={`w-3.5 h-3.5 ${recalculating ? 'animate-pulse' : ''}`} />
              {recalculating ? 'Recalculating...' : 'Recalc Performance'}
            </button>
            <button onClick={fetchUsers} className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
              <RefreshCw className={`w-4 h-4 text-slate-400 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 mt-3">
            <button
              onClick={() => setActiveTab('users')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-11 font-medium rounded-lg transition-all ${
                activeTab === 'users'
                  ? 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/30'
                  : 'text-slate-500 hover:text-slate-300 border border-transparent'
              }`}
            >
              <Users className="w-3.5 h-3.5" />
              Users
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-4">
        {/* Recalculation results banner */}
        {recalcResult && (
          <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl">
            <p className="text-11 text-amber-400 font-semibold mb-2">Performance Recalculated</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {recalcResult.map((r: any) => (
                <div key={r.agent_id} className="text-11 text-slate-300">
                  <span className="text-white font-medium">{r.agent_id}</span>: {r.total_trades} trades, {r.win_rate?.toFixed(0)}% WR
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <>
            {loading ? (
              <div className="flex justify-center py-20">
                <RefreshCw className="w-6 h-6 animate-spin text-slate-600" />
              </div>
            ) : (
              <>
              {/* 7.3 mobile: one card per user. A 6-column table can only survive 390px by
                  scrolling horizontally, which the spec forbids — so below `md` the same
                  fields render stacked, with NO data dropped. */}
              <div className="md:hidden divide-y divide-slate-800/50">
                {users.map((user) => (
                  <div key={user.uid} className="py-3 px-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-white truncate">{user.name || '\u2014'}</div>
                        <div className="text-11 text-slate-500 truncate">{user.email}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`text-10 px-2 py-0.5 rounded-md font-bold ${
                          user.role === 'admin'
                            ? 'bg-red-500/15 text-red-400 border border-red-500/30'
                            : 'bg-slate-700/50 text-slate-400 border border-slate-600/30'
                        }`}>
                          {user.role?.toUpperCase() || 'USER'}
                        </span>
                        <button
                          onClick={() => toggleNewsletter(user)}
                          className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded touch-manipulation hover:bg-slate-700/50 transition-colors"
                          title={user.newsletter_enabled !== false ? 'Disable notifications' : 'Enable notifications'}
                        >
                          {user.newsletter_enabled !== false
                            ? <Bell className="w-4 h-4 text-emerald-400" />
                            : <BellOff className="w-4 h-4 text-slate-600" />}
                        </button>
                      </div>
                    </div>
                    {/* mobile-ok: three short date/count pairs, ~119px each at 390px */}
                    <div className="mt-2 grid grid-cols-3 gap-2 text-11">
                      <div><span className="block text-10 text-slate-500">Created</span><span className="text-slate-300">{formatDate(user.created_at)}</span></div>
                      <div><span className="block text-10 text-slate-500">Last Login</span><span className="text-slate-300">{timeAgo(user.last_login)}</span></div>
                      <div><span className="block text-10 text-slate-500">Logins</span><span className="text-slate-300 font-mono">{user.login_count || 0}</span></div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-slate-800">
                      <th className="px-3 py-2.5 text-10 text-slate-500 uppercase tracking-wider font-semibold">User</th>
                      <th className="px-3 py-2.5 text-10 text-slate-500 uppercase tracking-wider font-semibold">Role</th>
                      <th className="px-3 py-2.5 text-10 text-slate-500 uppercase tracking-wider font-semibold">Created</th>
                      <th className="px-3 py-2.5 text-10 text-slate-500 uppercase tracking-wider font-semibold">Last Login</th>
                      <th className="px-3 py-2.5 text-10 text-slate-500 uppercase tracking-wider font-semibold text-right">Logins</th>
                      <th className="px-3 py-2.5 text-10 text-slate-500 uppercase tracking-wider font-semibold text-center">Newsletter</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/50">
                    {users.map((user) => (
                      <tr key={user.uid} className="hover:bg-slate-900/60 transition-colors">
                        <td className="px-3 py-3">
                          <div className="text-sm font-medium text-white">{user.name || '—'}</div>
                          <div className="text-11 text-slate-500">{user.email}</div>
                        </td>
                        <td className="px-3 py-3">
                          <span className={`text-10 px-2 py-0.5 rounded-md font-bold ${
                            user.role === 'admin'
                              ? 'bg-red-500/15 text-red-400 border border-red-500/30'
                              : 'bg-slate-700/50 text-slate-400 border border-slate-600/30'
                          }`}>
                            {user.role?.toUpperCase() || 'USER'}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-xs text-slate-400">{formatDate(user.created_at)}</td>
                        <td className="px-3 py-3 text-xs text-slate-400">{timeAgo(user.last_login)}</td>
                        <td className="px-3 py-3 text-xs text-slate-300 text-right font-mono">{user.login_count || 0}</td>
                        <td className="px-3 py-3 text-center">
                          <button
                            onClick={() => toggleNewsletter(user)}
                            className="hover:bg-slate-700/50 rounded p-1 transition-colors cursor-pointer"
                            title={user.newsletter_enabled !== false ? 'Disable notifications' : 'Enable notifications'}
                          >
                            {user.newsletter_enabled !== false ? (
                              <Bell className="w-3.5 h-3.5 text-emerald-400 mx-auto" />
                            ) : (
                              <BellOff className="w-3.5 h-3.5 text-slate-600 mx-auto" />
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
